import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import analyticsIcon from '../assets/analytics.svg';
import emissionsIcon from '../assets/emissions.svg';
import scope1Icon from '../assets/scope1.svg';
import scope2Icon from '../assets/scope2.svg';
import scope3Icon from '../assets/scope3.svg';
import invoicesIcon from '../assets/invoices.svg';
import reportsIcon from '../assets/reports.svg';
import buildingIcon from '../assets/building.svg';
import accountIcon from '../assets/account.svg';
import logoImage from '../assets/image.png';
// Sub-scope icons
import nxemjetIcon from '../assets/nxemjet.svg';
import veturatElektrikeIcon from '../assets/veturat-elektrike.svg';
import elektricitetiIcon from '../assets/elektriciteti.svg';

export const Sidebar = ({ onNavigate }) => {
  const navigate = useNavigate();
  const pathname = window.location.pathname;
  
  // Get user from token
  const [userEmail, setUserEmail] = useState('');
  
  useEffect(() => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserEmail(payload.email || '');
      }
    } catch (err) {
      console.error('Failed to decode token:', err);
    }
  }, []);
  
  const ADMIN_EMAILS = ['admin@karbon360.com', 'testuser123@example.com'];
  const isAdmin = ADMIN_EMAILS.includes(userEmail);
  
  // State for expandable sections
  // Note: /invoices is Scope 2 electricity, /invoices-new is the standalone Invoices tab
  const [emissionsOpen, setEmissionsOpen] = useState(
    pathname.includes('/scope-')
  );
  const [scope1Open, setScope1Open] = useState(pathname.includes('/scope-1'));
  const [scope2Open, setScope2Open] = useState(
    pathname.includes('/scope-2')
  );

  useEffect(() => {
    if (pathname.includes('/scope-')) {
      setEmissionsOpen(true);
    }
    if (pathname.includes('/scope-1')) {
      setScope1Open(true);
    }
    if (pathname.includes('/scope-2')) {
      setScope2Open(true);
    }
  }, [pathname]);

  const handleLogout = () => {
    try { 
      localStorage.removeItem('token'); 
    } catch {}
    if (onNavigate) onNavigate();
    navigate('/');
  };

  // Handle navigation click - close mobile menu
  const handleNavClick = () => {
    if (onNavigate) onNavigate();
  };

  const isActive = (path) => pathname === path;
  const isParentActive = (paths) => paths.some(p => pathname.startsWith(p));

  return (
    <div className="sidebar-content">
      {/* Logo/Brand */}
      <div className="sidebar-header">
        <div className="sidebar-logo-wrapper">
          <img src={logoImage} alt="Logo" className="sidebar-logo" />
        </div>
        <div className="sidebar-brand">Karbon360°</div>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        
        {/* Kalkulo Section */}
        <div className="nav-section-title">Kalkulo</div>

        <NavLink to="/regjistrimi" className={`nav-item ${isActive('/regjistrimi') ? 'active' : ''}`} onClick={handleNavClick}>
          <img src={analyticsIcon} alt="" className="nav-icon" />
          <span className="nav-label">Regjistrimi mujor</span>
        </NavLink>

        {/* Emissions Section */}
        <div className="nav-section">
          <button 
            className={`nav-item nav-parent ${emissionsOpen ? 'open' : ''}`}
            onClick={() => setEmissionsOpen(!emissionsOpen)}
          >
            <img src={emissionsIcon} alt="" className="nav-icon" />
            <span className="nav-label">Emissions</span>
            <span className={`nav-arrow ${emissionsOpen ? 'open' : ''}`}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M4 5L6 7L8 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
          </button>

          {emissionsOpen && (
            <div className="nav-children">
              {/* Scope 1 */}
              <div className="nav-subsection">
                <button
                  className="nav-item nav-scope"
                  onClick={() => setScope1Open(!scope1Open)}
                >
                  <img src={scope1Icon} alt="" className="nav-icon" />
                  <span className="nav-label">Scope 1</span>
                  <span className={`nav-arrow ${scope1Open ? 'open' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M5 4L7 6L5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>

                {scope1Open && (
                  <div className="nav-scope-children">
                    <NavLink 
                      to="/scope-1/djegja-stacionare" 
                      className={`nav-item nav-sub ${isActive('/scope-1/djegja-stacionare') ? 'active' : ''}`}
                      onClick={handleNavClick}
                    >
                      <img src={nxemjetIcon} alt="" className="nav-icon" />
                      <span className="nav-label">Djegja stacionare</span>
                    </NavLink>
                    <NavLink 
                      to="/scope-1/djegja-mobile" 
                      className={`nav-item nav-sub ${isActive('/scope-1/djegja-mobile') ? 'active' : ''}`}
                      onClick={handleNavClick}
                    >
                      <img src={veturatElektrikeIcon} alt="" className="nav-icon" />
                      <span className="nav-label">Djegja mobile</span>
                    </NavLink>
                    {/* Emetimet fugjitive - hidden for now */}
                    <NavLink 
                      to="/scope-1/emetimet-nga-proceset" 
                      className={`nav-item nav-sub ${isActive('/scope-1/emetimet-nga-proceset') ? 'active' : ''}`}
                      onClick={handleNavClick}
                    >
                      <img src={emissionsIcon} alt="" className="nav-icon" />
                      <span className="nav-label">Emetimet nga proceset</span>
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Scope 2 */}
              <div className="nav-subsection">
                <button
                  className="nav-item nav-scope"
                  onClick={() => setScope2Open(!scope2Open)}
                >
                  <img src={scope2Icon} alt="" className="nav-icon" />
                  <span className="nav-label">Scope 2</span>
                  <span className={`nav-arrow ${scope2Open ? 'open' : ''}`}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M5 4L7 6L5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>

                {scope2Open && (
                  <div className="nav-scope-children">
                    <NavLink 
                      to="/scope-2/electricity" 
                      className={`nav-item nav-sub ${isActive('/scope-2/electricity') ? 'active' : ''}`}
                      onClick={handleNavClick}
                    >
                      <img src={elektricitetiIcon} alt="" className="nav-icon" />
                      <span className="nav-label">Electricity</span>
                    </NavLink>
                  </div>
                )}
              </div>

              {/* Scope 3 */}
              <NavLink
                to="/scope-3"
                className={`nav-item nav-scope ${isParentActive(['/scope-3']) ? 'active' : ''}`}
                onClick={handleNavClick}
              >
                <img src={scope3Icon} alt="" className="nav-icon" />
                <span className="nav-label">Scope 3</span>
              </NavLink>
            </div>
          )}
        </div>

        {/* Menaxho Section */}
        <div className="nav-section-title">Menaxho</div>
        
        <NavLink to="/invoices-new" className={`nav-item ${isActive('/invoices-new') ? 'active' : ''}`} onClick={handleNavClick}>
          <img src={invoicesIcon} alt="" className="nav-icon" />
          <span className="nav-label">Invoices</span>
        </NavLink>

        {/* Raportet Section */}
        <div className="nav-section-title">Raportet</div>
        
        <NavLink to="/emissions" end className={`nav-item ${isActive('/emissions') && !isActive('/emissions/detailed') ? 'active' : ''}`} onClick={handleNavClick}>
          <img src={reportsIcon} alt="" className="nav-icon" />
          <span className="nav-label">Reports</span>
        </NavLink>

        <NavLink to="/emissions/detailed" className={`nav-item ${isActive('/emissions/detailed') ? 'active' : ''}`} onClick={handleNavClick}>
          <img src={reportsIcon} alt="" className="nav-icon" />
          <span className="nav-label">Raporti i detajuar</span>
        </NavLink>

        {/* Facilities Section */}
        <div className="nav-section-title">Facilities</div>
        <NavLink to="/buildings" className={`nav-item ${isActive('/buildings') ? 'active' : ''}`} onClick={handleNavClick}>
          <img src={buildingIcon} alt="" className="nav-icon" />
          <span className="nav-label">Buildings</span>
        </NavLink>

      </nav>

      {/* Footer - Admin Panel (hidden per request) + Logout */}
      <div className="sidebar-footer">
        <button className="nav-item" onClick={handleLogout}>
          <svg className="nav-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ opacity: 0.7 }}>
            <path d="M13 3H16C16.5304 3 17.0391 3.21071 17.4142 3.58579C17.7893 3.96086 18 4.46957 18 5V15C18 15.5304 17.7893 16.0391 17.4142 16.4142C17.0391 16.7893 16.5304 17 16 17H13M8 13L12 9M12 9L8 5M12 9H2" 
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span className="nav-label">Logout</span>
        </button>
      </div>
    </div>
  );
};
